import Icon from 'components/atoms/Icon';
import { useField, useFormikContext } from 'formik';
import {
  SoftSkillGranularity,
  useSoftSkillFindManyPaginatedQuery,
} from 'generated/graphql';
import { debounce } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import { FC, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { TEditSoftSkillFormValues } from 'views/StaticContent/SoftSkills/drawers/SoftSkillDrawer.helpers';
import { QueryParams } from 'views/StaticContent/SoftSkills/SoftSkills.helpers';
import {
  Button,
  IFormikSelectFormField,
  SelectFormField,
  TNotification,
  TSelectOption,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './SoftSkillRelations.module.scss';

interface ISoftSkillRelationSelectFormField
  extends Omit<IFormikSelectFormField, 'options' | 'useFormikField'> {
  isEditMode: boolean;
  index: number;
  isBroadSoftSkill: boolean;
  relationType: 'vertical' | 'horizontal';
  onDelete: () => void;
}

type PaginationStateType = Record<
  string,
  { currentPage?: number; totalPages?: number }
>;

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_TIME = 500;

export const SoftSkillRelationSelectFormField: FC<ISoftSkillRelationSelectFormField> =
  ({
    id,
    isBroadSoftSkill,
    isEditMode,
    index,
    relationType,
    onDelete,
    ...rest
  }) => {
    const { handleMsgType } = useNotification();
    const location = useLocation();

    const openSoftSkillInNewTab = (softSkillSubId: string | undefined) => {
      if (!softSkillSubId) return;
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(QueryParams.EDIT_DRAWER, softSkillSubId);

      const newUrl = `${window.location.origin}${
        location.pathname
      }?${searchParams.toString()}`;

      window.open(newUrl, '_blank');
    };

    const [isLoading, setIsLoading] = useState(false);
    const { values, setFieldValue } =
      useFormikContext<TEditSoftSkillFormValues>();

    const [softSkills, setSoftSkills] = useState<
      { label: string; value: string }[]
    >(
      relationType === 'horizontal'
        ? values.similarSoftSkills
        : isBroadSoftSkill
        ? values.focusedSoftSkills
        : values.broadSoftSkills
    );
    const [pagination, setPagination] = useState<PaginationStateType>({
      '': {
        currentPage: 1,
        totalPages: undefined,
      },
    });

    const [searchValue, setSearchValue] = useState('');
    const [field, meta] = useField<TSelectOption>(id);

    const handleChange = (newValues: TSelectOption) => {
      if (newValues.value === field.value.value) return;

      const selectedRelation = softSkills.find(
        relationOption => relationOption.value === newValues.value
      );

      if (!selectedRelation) return;
      setFieldValue(
        relationType === 'horizontal'
          ? `similarSoftSkills.${index}`
          : isBroadSoftSkill
          ? `focusedSoftSkills.${index}`
          : `broadSoftSkills.${index}`,
        selectedRelation
      );
    };

    const hasError = Boolean(meta.error && meta.touched);
    const error = meta.error as unknown as
      | { label: string; value: string }
      | undefined;

    useSoftSkillFindManyPaginatedQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        filters: {
          companyId: values.owner.value,
          name: searchValue,
          granularity:
            relationType === 'horizontal'
              ? isBroadSoftSkill
                ? SoftSkillGranularity.Broad
                : SoftSkillGranularity.Focused
              : isBroadSoftSkill
              ? SoftSkillGranularity.Focused
              : SoftSkillGranularity.Broad,
        },
        pagination: {
          page: pagination[searchValue]?.currentPage
            ? pagination[searchValue].currentPage
            : 1,
          size: PAGE_SIZE,
        },
      },
      onCompleted: data => {
        const loadedSkills = (data?.SoftSkillFindManyPaginated.data || []).map(
          ({ subId, name }) => ({ value: subId, label: name })
        );

        const newSoftSkills = uniqBy([...loadedSkills, ...softSkills], 'value');

        const pageInfo = data?.SoftSkillFindManyPaginated.pageInfo;

        setPagination(prev => ({
          ...prev,
          [searchValue]: {
            currentPage: pageInfo?.currentPage,
            totalPages: pageInfo?.pageTotal,
          },
        }));
        setSoftSkills(newSoftSkills);
        setIsLoading(false);
      },
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
        setIsLoading(false);
      },
    });

    const searchSoftSkills = async (inputValue: string) => {
      if (inputValue === searchValue || !inputValue) return;
      setIsLoading(true);
      setSearchValue(inputValue);
    };

    const debouncedSearchSoftSkills = debounce(
      searchSoftSkills,
      SEARCH_DEBOUNCE_TIME
    );

    const handleLoadMore = async () => {
      setIsLoading(true);
      const currentPage = pagination[searchValue]?.currentPage;
      const newPage = currentPage ? currentPage + 1 : 1;
      setPagination(prev => ({
        ...prev,
        [searchValue]: {
          currentPage: newPage,
          totalPages: pagination[searchValue]?.totalPages,
        },
      }));
    };

    const options = useMemo(() => {
      if (!softSkills || isLoading) return [];

      // Parse soft skills to options.
      const parsedOptions = softSkills.map(({ label, value }) => ({
        value,
        label,
        labelDescription: value,
      }));

      // Remove skills that are already selected in other rows from the options.
      const otherSelectedSoftSkillIds = (
        relationType === 'horizontal'
          ? values.similarSoftSkills
          : isBroadSoftSkill
          ? values.focusedSoftSkills
          : values.broadSoftSkills
      )
        .map(otherRelation => otherRelation.value)
        .filter(
          otherRelation => otherRelation && otherRelation !== field.value.value
        );

      const filteredOptions = parsedOptions.filter(option => {
        return !otherSelectedSoftSkillIds.includes(option.value);
      });

      // Sort options alphabetically.
      return filteredOptions.sort((a, b) =>
        a.label.toLowerCase() < b.label.toLowerCase() ? -1 : 1
      );
    }, [
      isBroadSoftSkill,
      softSkills,
      isLoading,
      field.value.value,
      values,
      relationType,
    ]);

    const value = useMemo(
      () => options.find(option => option.value === field.value.value),
      [options, field.value.value]
    );
    const currentPage = pagination[searchValue]?.currentPage;
    const totalPages = pagination[searchValue]?.totalPages;
    const loadMore = currentPage && totalPages && currentPage < totalPages;

    return (
      <div className={styles.softSkillRelationContainer}>
        <SelectFormField
          {...rest}
          id={id}
          className={styles.selectField}
          value={value}
          onChange={handleChange}
          onInputChange={debouncedSearchSoftSkills}
          onLoadMore={handleLoadMore}
          loadMore={Boolean(loadMore)}
          options={options}
          hasError={hasError}
          bottomText={hasError ? error?.value : ''}
          hasClearIndicator={false}
          loadMoreButtonText="Load more results"
          isLoading={isLoading}
          isDisabled={!isEditMode}
          menuPortalTarget={null}
        />

        {isEditMode && (
          <Button
            onClick={onDelete}
            size="medium"
            variant="negative"
            type="button"
            leftIcon={
              <Icon icon="delete_outline" className={styles.deleteIcon} />
            }
          />
        )}
        <Button
          size="medium"
          variant="tertiary"
          leftIcon={<Icon icon="openTab" />}
          className={styles.openTabIcon}
          onClick={() => openSoftSkillInNewTab(field.value.value)}
        />
      </div>
    );
  };
